import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

import {
  Box,
  Center,
  Flex,
  Grid,
  GridItem,
  Text,
  Tooltip,
  useBreakpointValue,
} from '@chakra-ui/react'
import { IAgoraRTCRemoteUser, UID, useRemoteUsers } from 'agora-rtc-react'
import { useVideoCallUsers } from 'src/features/videoCall/api/getVideoCallUsers'

import { useVideoCallRoom } from '../../context/videoCallRoomContext'
import { isScreenShareUserUid } from '../../utils/screenShare'
import { SharedScreenPlayer } from '../SharedScreenPlayer/SharedScreenPlayer'
import { VideoCallRoomLocalPlayer } from '../VideoCallRoomLocalPlayer/VideoCallRoomLocalPlayer'
import { VideoCallRoomRemotePlayer } from '../VideoCallRoomRemotePlayer/VideoCallRoomRemotePlayer'

type Props = {
  colorScheme?: 'blue' | 'green'
  mainUser: IAgoraRTCRemoteUser | undefined
  onSelectMainUser: (uid: UID) => void
}

export const ScreenSharingModeMainContent: React.FC<Props> = ({
  colorScheme,
  mainUser,
  onSelectMainUser,
}) => {
  const remoteUsers = useRemoteUsers()
  const { videoCallUserUid } = useVideoCallRoom()

  const { data: videoCallUsers, mutate } = useVideoCallUsers({
    videoCallUserUids: remoteUsers.map(user => user.uid as number),
  })

  // remoteUsersが変化したときにvideoCallUsersを再取得する
  useEffect(() => {
    mutate()
  }, [remoteUsers, mutate])

  const containerRef = useRef<HTMLDivElement>(null)

  const playerWidth = useBreakpointValue({ base: 160, lg: 240 }) ?? 160
  const playerHeight = useBreakpointValue({ base: 90, lg: 135 }) ?? 90

  const isLargerThanLg = useBreakpointValue({ base: false, lg: true }) ?? false

  const gridAutoFlow =
    useBreakpointValue({ base: 'column', lg: 'row' }) ?? 'row'

  const subscribe = useCallback((onStoreChange: () => void) => {
    const observer = new ResizeObserver(entries => {
      entries.forEach(() => onStoreChange())
    })
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }
    return () => observer.disconnect()
  }, [])

  const calculateShowableRemoteUserLength = useCallback(() => {
    if (!containerRef.current) return 0
    const containerHeight = isLargerThanLg
      ? containerRef.current.clientHeight
      : containerRef.current.clientWidth
    const targetSize = isLargerThanLg ? playerHeight : playerWidth
    const floored = Math.floor((containerHeight - targetSize) / targetSize)
    return Math.max(floored, 0)
  }, [isLargerThanLg, playerHeight, playerWidth])

  const maxShowableBlockSize = useSyncExternalStore(
    subscribe,
    calculateShowableRemoteUserLength,
  )

  const filteredRemoteUsers = remoteUsers.filter(
    user => user.uid !== mainUser?.uid,
  )

  const userCount = filteredRemoteUsers.length

  // 画面共有ユーザーは常に表示し、通常ユーザーが見切れるようにする
  const screenShareSidebarUsers = filteredRemoteUsers.filter(user =>
    isScreenShareUserUid(user.uid),
  )
  const normalSidebarUsers = filteredRemoteUsers.filter(
    user => !isScreenShareUserUid(user.uid),
  )
  const remainingSlotsForNormal = Math.max(
    maxShowableBlockSize - screenShareSidebarUsers.length,
    0,
  )
  const hasHiddenNormalUsers =
    normalSidebarUsers.length > remainingSlotsForNormal
  const normalUsersToShow = hasHiddenNormalUsers
    ? Math.max(remainingSlotsForNormal - 1, 0) // 「他X人」表示のためにスロットを1つ確保
    : normalSidebarUsers.length
  const showableSidebarUsers = [
    ...screenShareSidebarUsers,
    ...normalSidebarUsers.slice(0, normalUsersToShow),
  ]
  const hiddenUserCount = userCount - showableSidebarUsers.length

  return (
    <Grid
      ref={containerRef}
      autoFlow={gridAutoFlow}
      boxSize="full"
      gridTemplateColumns={{ base: undefined, lg: '1fr max-content' }}
      gridTemplateRows={{ base: '1fr max-content', lg: undefined }}
      borderRadius="base"
    >
      <GridItem>
        {!!mainUser &&
          (isScreenShareUserUid(mainUser.uid) ? (
            <SharedScreenPlayer
              screenSharingRemoteUser={mainUser}
              colorScheme={colorScheme}
              currentUserUid={videoCallUserUid ?? undefined}
            />
          ) : (
            <VideoCallRoomRemotePlayer
              remoteUser={mainUser}
              userName={
                videoCallUsers?.find(
                  u => u.videoCallUserUid === (mainUser.uid as number),
                )?.name
              }
            />
          ))}
      </GridItem>
      <GridItem h="full" pl="1" overflow="hidden">
        <Flex
          h="full"
          flexDirection={{ base: 'row-reverse', lg: 'column-reverse' }}
          justify="end"
          gap="1"
        >
          <Box w={playerWidth} h={playerHeight}>
            <VideoCallRoomLocalPlayer isSmall hideBadge />
          </Box>
          {maxShowableBlockSize > 0 &&
            showableSidebarUsers.map(user => (
              <Tooltip
                key={user.uid}
                label="メイン画面に固定"
                placement="left"
                hasArrow
              >
                <Box
                  as="button"
                  type="button"
                  w={`${playerWidth}px`}
                  h={`${playerHeight}px`}
                  cursor="pointer"
                  onClick={() => onSelectMainUser(user.uid)}
                  _hover={{ opacity: 0.85 }}
                  bg="transparent"
                  border="none"
                  p="0"
                >
                  {isScreenShareUserUid(user.uid) ? (
                    <SharedScreenPlayer
                      screenSharingRemoteUser={user}
                      colorScheme={colorScheme}
                      currentUserUid={videoCallUserUid ?? undefined}
                      isSmall
                    />
                  ) : (
                    <VideoCallRoomRemotePlayer
                      remoteUser={user}
                      isSmall
                      userName={
                        videoCallUsers?.find(
                          u => u.videoCallUserUid === (user.uid as number),
                        )?.name
                      }
                    />
                  )}
                </Box>
              </Tooltip>
            ))}

          {hiddenUserCount > 0 && (
            <Center
              maxH={`${playerHeight}px`}
              maxW={`${playerWidth}px`}
              boxSize="full"
              bg="gray.100"
            >
              <Text fontSize="2xl" fontWeight="bold" color="white">
                他{hiddenUserCount}人
              </Text>
            </Center>
          )}
        </Flex>
      </GridItem>
    </Grid>
  )
}
